/**
 * Division versioning display rules (#937). Dates are YYYY-MM-DD calendar days.
 *
 * Saving a Division edits its most recent version in place when that version has
 * not taken effect yet (effective after today); otherwise it creates the next
 * version. The server decides; these helpers only predict it for the form.
 */
import type { Division, DivisionLeadInfo, DivisionLeadsGrouped, DivisionVersion } from '@app-types/division';
import { addDaysToDate } from '@utils/dateUtils';

/** Days after today a new version becomes effective unless another date is chosen (server default). */
export const NEW_VERSION_EFFECTIVE_OFFSET_DAYS = 30;

export type LeadLevel = keyof DivisionLeadsGrouped;

const LEAD_LEVELS: LeadLevel[] = ['l1', 'l2', 'l3'];

/**
 * A YYYY-MM-DD date as zero-padded MM/DD/YYYY, matching the Effective Date input (ADO-1157).
 * Read as written, with no Date parsing, so the calendar day never shifts for viewers behind UTC.
 */
export const formatVersionDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
};

/** The version a save starts from: the Future Version if there is one, else the Current Version. */
export const latestVersion = (division: Division): DivisionVersion =>
  division.future_version ?? division.current_version;

/** Whether saving edits the most recent version in place (it takes effect after today). */
export const editsInPlace = (division: Division, today: string): boolean =>
  latestVersion(division).effective_date > today;

/** The Effective Date the form starts with: the pending version's own date, or today + 30 for a new version. */
export const defaultEffectiveDate = (division: Division, today: string): string =>
  editsInPlace(division, today)
    ? latestVersion(division).effective_date
    : addDaysToDate(today, NEW_VERSION_EFFECTIVE_OFFSET_DAYS);

/** The sentence the edit form shows before saving. */
export const describeSaveOutcome = (division: Division, effectiveDate: string, today: string): string => {
  const latest = latestVersion(division);
  if (editsInPlace(division, today)) {
    return `Editing future version ${latest.version} (effective ${formatVersionDate(effectiveDate)})`;
  }
  return `This will create version ${latest.version + 1}, effective ${formatVersionDate(effectiveDate)}`;
};

/** One level's leads as a list (L1 holds a single lead or none). */
export const leadsAtLevel = (leads: DivisionLeadsGrouped, level: LeadLevel): DivisionLeadInfo[] => {
  const value = leads[level];
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const levelUserIds = (leads: DivisionLeadsGrouped, level: LeadLevel): number[] =>
  leadsAtLevel(leads, level)
    .map((lead) => lead.user_id)
    .sort((a, b) => a - b);

/** Lead levels whose users differ between two versions, ignoring order. */
export const changedLeadLevels = (from: DivisionVersion, to: DivisionVersion): LeadLevel[] =>
  LEAD_LEVELS.filter((level) => levelUserIds(from.leads, level).join(',') !== levelUserIds(to.leads, level).join(','));

/** One level's lead display names joined by ", ", or "none". */
export const leadNames = (leads: DivisionLeadsGrouped, level: LeadLevel): string => {
  const atLevel = leadsAtLevel(leads, level);
  return atLevel.length > 0 ? atLevel.map((l) => l.display_name).join(', ') : 'none';
};

/** A field the Future Version changes, with its future value. */
export interface FutureVersionChange {
  label: string;
  value: string;
}

const LEAD_LEVEL_LABELS: Record<LeadLevel, string> = { l1: 'Lead L1', l2: 'Lead L2', l3: 'Lead L3' };

const normalizedText = (text: string | null): string => (text ?? '').trim();

/**
 * What the Future Version changes against the Current Version, in list-column order
 * (ADO-1159). Name and description compare trimmed, with null and '' equal.
 */
export const futureVersionChanges = (current: DivisionVersion, future: DivisionVersion): FutureVersionChange[] => {
  const changes: FutureVersionChange[] = [];
  if (normalizedText(current.name) !== normalizedText(future.name)) {
    changes.push({ label: 'Division Name', value: future.name });
  }
  if (normalizedText(current.description) !== normalizedText(future.description)) {
    changes.push({ label: 'Description', value: normalizedText(future.description) || 'none' });
  }
  for (const level of changedLeadLevels(current, future)) {
    changes.push({ label: LEAD_LEVEL_LABELS[level], value: leadNames(future.leads, level) });
  }
  return changes;
};
